import { contactConnectedDate, contactSeniority, seniorityGroupLabels, type Contact, type OrganisationCandidate, type SeniorityGroup } from "./domain";

export type RelationshipNodeType = "person" | "organisation" | "position" | "industry" | "community" | "memory" | "brief" | "opportunity" | "follow-up" | "interaction" | "theme";
export type NetworkPersonSort = "name-asc" | "name-desc" | "date-desc" | "date-asc" | "strength-desc" | "strength-asc";
export type OrganisationSector = "financial" | "healthcare" | "government" | "professional" | "technology" | "industrial" | "consumer" | "education" | "other";

export const organisationSectors: Array<{ id: OrganisationSector; label: string; color: string }> = [
  { id: "financial", label: "Financial Services", color: "#005eb8" },
  { id: "healthcare", label: "Healthcare", color: "#00856a" },
  { id: "government", label: "Government", color: "#6f42c1" },
  { id: "professional", label: "Professional Services", color: "#c43d4f" },
  { id: "technology", label: "Technology", color: "#007c91" },
  { id: "industrial", label: "Industrial", color: "#8c6d1f" },
  { id: "consumer", label: "Consumer", color: "#df641c" },
  { id: "education", label: "Education / Nonprofit", color: "#51752b" },
  { id: "other", label: "Other", color: "#59636e" },
];

export function classifyOrganisationSector(organisation: OrganisationCandidate): OrganisationSector {
  const value = [organisation.canonicalName, ...organisation.aliases, ...organisation.industries].join(" ").toLocaleLowerCase("en");
  if (/\b(government|ministry|municipal|public service|statutory board|regulator|regulatory|authority|commission|embassy|parliament)\b/.test(value)) return "government";
  if (/\b(bank|banking|insurance|insurer|financial|finance|fintech|capital|asset management|wealth|securities|investment|fund|payments?)\b/.test(value)) return "financial";
  if (/\b(health|healthcare|hospital|medical|medicine|pharma|pharmaceutical|biotech|clinic|diagnostic)\b/.test(value)) return "healthcare";
  if (/\b(consulting|consultancy|advisory|accounting|audit|legal|law firm|professional services|deloitte|pwc|kpmg|ey|ernst|mckinsey|bcg|bain)\b/.test(value)) return "professional";
  if (/\b(technology|software|digital|cloud|cyber|semiconductor|electronics|telecom|data|artificial intelligence|information technology)\b/.test(value)) return "technology";
  if (/\b(manufactur|industrial|engineering|construction|energy|oil|gas|mining|logistics|shipping|aerospace|automotive|infrastructure)\b/.test(value)) return "industrial";
  if (/\b(consumer|retail|food|beverage|hospitality|hotel|travel|media|entertainment|fashion|restaurant|real estate|property)\b/.test(value)) return "consumer";
  if (/\b(university|school|education|academy|institute|foundation|charity|nonprofit|non-profit|association)\b/.test(value)) return "education";
  return "other";
}

export function sectorColor(sector?: OrganisationSector): string {
  return organisationSectors.find((item) => item.id === sector)?.color ?? organisationSectors.at(-1)!.color;
}

export interface RelationshipNode {
  id: string;
  type: RelationshipNodeType;
  label: string;
  subtitle: string;
  contact?: Contact;
  organisation?: OrganisationCandidate;
  seniorityGroup?: SeniorityGroup;
  sector?: OrganisationSector;
  communityId?: string;
  communityColor?: string;
  canonicalId?: string;
  graphType?: string;
  bridgeScore?: number;
  degree?: number;
  connectionCount: number;
}

export interface RelationshipEdge {
  id: string;
  source: string;
  target: string;
  type: "affiliation" | "position-group" | "industry-group" | "community-membership" | "graph-relation";
  derived: true;
  evidenceType: "extracted" | "inferred" | "ambiguous";
  approvalState: "verified" | "derived" | "pending";
  confidence: number;
  sourcePath: string;
  evidenceExcerpt: string;
  connectedAt?: string;
  recencyBand?: ConnectionRecencyBand;
}

export type ConnectionRecencyBand = "under-1" | "1-3" | "3-5" | "over-5" | "unknown";
export const recencyStyles: Record<ConnectionRecencyBand, { label: string; color: string; opacity: number; distance: number }> = {
  "under-1": { label: "Under 1 year", color: "#2f8f46", opacity: 0.9, distance: 58 },
  "1-3": { label: "1-3 years", color: "#1686a0", opacity: 0.72, distance: 78 },
  "3-5": { label: "3-5 years", color: "#b07a16", opacity: 0.56, distance: 98 },
  "over-5": { label: "Over 5 years", color: "#777f86", opacity: 0.38, distance: 122 },
  unknown: { label: "Unknown date", color: "#aeb3ae", opacity: 0.25, distance: 142 },
};

export function connectionRecency(date: Date | null, now = new Date()): ConnectionRecencyBand {
  if (!date) return "unknown";
  const years = (now.getTime() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  if (years < 1) return "under-1";
  if (years < 3) return "1-3";
  if (years < 5) return "3-5";
  return "over-5";
}

export function sortRelationshipPeople(nodes: RelationshipNode[], order: NetworkPersonSort): RelationshipNode[] {
  return [...nodes].sort((left, right) => {
    const nameOrder = left.label.localeCompare(right.label, "en", { sensitivity: "base" });
    if (order === "name-asc") return nameOrder;
    if (order === "name-desc") return -nameOrder;
    if (order === "date-asc" || order === "date-desc") {
      const leftDate = left.contact ? contactConnectedDate(left.contact)?.getTime() : undefined;
      const rightDate = right.contact ? contactConnectedDate(right.contact)?.getTime() : undefined;
      if (leftDate == null && rightDate == null) return nameOrder;
      if (leftDate == null) return 1;
      if (rightDate == null) return -1;
      return order === "date-asc" ? leftDate - rightDate || nameOrder : rightDate - leftDate || nameOrder;
    }
    const leftStrength = left.contact?.relationshipStrength || 0;
    const rightStrength = right.contact?.relationshipStrength || 0;
    if (!leftStrength && !rightStrength) return nameOrder;
    if (!leftStrength) return 1;
    if (!rightStrength) return -1;
    return order === "strength-asc" ? leftStrength - rightStrength || nameOrder : rightStrength - leftStrength || nameOrder;
  });
}

export function buildPositionGraph(contacts: Contact[]): RelationshipGraphData {
  const groups = new Map<SeniorityGroup, Contact[]>();
  for (const contact of contacts) {
    const group = contactSeniority(contact);
    const members = groups.get(group) ?? [];
    members.push(contact); groups.set(group, members);
  }
  const nodes: RelationshipNode[] = [];
  const edges: RelationshipEdge[] = [];
  for (const [group, members] of groups) {
    const groupId = `position:${group}`;
    nodes.push({ id: groupId, type: "position", label: seniorityGroupLabels[group], subtitle: `${members.length.toLocaleString()} people`, seniorityGroup: group, connectionCount: members.length });
    for (const contact of members) {
      const personId = `person:${contact.id}`;
      nodes.push({ id: personId, type: "person", label: contact.displayName, subtitle: contact.title || contact.title2 || "Role not set", contact, connectionCount: 1 });
      edges.push({ id: `${groupId}:${personId}`, source: personId, target: groupId, type: "position-group", derived: true, evidenceType: "inferred", approvalState: "derived", confidence: 0.75, sourcePath: `contacts/${contact.id}`, evidenceExcerpt: `Position grouping derived from ${contact.title || contact.title2 || "the saved job title"}.` });
    }
  }
  return { nodes, edges };
}

export function buildIndustryGraph(contacts: Contact[], organisations: OrganisationCandidate[]): RelationshipGraphData {
  const nodes = new Map<string, RelationshipNode>();
  const edges = new Map<string, RelationshipEdge>();
  const contactById = new Map(contacts.map((contact) => [contact.id, contact]));
  for (const organisation of organisations) {
    const sector = classifyOrganisationSector(organisation);
    const group = organisationSectors.find((item) => item.id === sector)!;
    const groupId = `industry:${sector}`;
    if (!nodes.has(groupId)) nodes.set(groupId, { id: groupId, type: "industry", label: group.label, subtitle: "Industry cluster", sector, connectionCount: 0 });
    for (const contactId of organisation.sourceContactIds) {
      const contact = contactById.get(contactId);
      if (!contact) continue;
      const personId = `person:${contactId}`;
      nodes.set(personId, { id: personId, type: "person", label: contact.displayName, subtitle: contact.title || contact.title2 || contact.company || "Contact", contact, connectionCount: 1 });
      const edgeId = `${groupId}:${personId}`;
      if (!edges.has(edgeId)) edges.set(edgeId, { id: edgeId, source: personId, target: groupId, type: "industry-group", derived: true, evidenceType: "inferred", approvalState: "derived", confidence: 0.78, sourcePath: `contacts/${contactId}`, evidenceExcerpt: `Industry grouping derived from ${organisation.canonicalName} and its saved industry information.` });
    }
  }
  for (const group of nodes.values()) {
    if (group.type === "industry") group.connectionCount = [...edges.values()].filter((edge) => edge.target === group.id).length;
  }
  return { nodes: [...nodes.values()], edges: [...edges.values()] };
}

export interface RelationshipGraphData {
  nodes: RelationshipNode[];
  edges: RelationshipEdge[];
}

export function relationshipPeopleForNode(
  data: RelationshipGraphData,
  nodeId: string,
  order: NetworkPersonSort,
): RelationshipNode[] {
  const connectedPersonIds = new Set<string>();
  for (const edge of data.edges) {
    if (edge.source !== nodeId && edge.target !== nodeId) continue;
    connectedPersonIds.add(edge.source === nodeId ? edge.target : edge.source);
  }

  return sortRelationshipPeople(
    data.nodes.filter((node) => node.type === "person" && connectedPersonIds.has(node.id)),
    order,
  );
}

export function buildRelationshipGraph(
  contacts: Contact[],
  organisations: OrganisationCandidate[],
): RelationshipGraphData {
  const nodes: RelationshipNode[] = [];
  const edges: RelationshipEdge[] = [];
  const connectedPeople = new Set<string>();
  const contactById = new Map(contacts.map((contact) => [contact.id, contact]));

  for (const organisation of organisations) {
    const organisationId = `organisation:${organisation.key}`;
    nodes.push({
      id: organisationId,
      type: "organisation",
      label: organisation.canonicalName,
      subtitle: `${organisation.contacts.length} ${organisation.contacts.length === 1 ? "person" : "people"}`,
      organisation,
      sector: classifyOrganisationSector(organisation),
      connectionCount: organisation.sourceContactIds.length,
    });
    for (const contactId of organisation.sourceContactIds) {
      const contact = contactById.get(contactId);
      if (!contact) continue;
      const connectedDate = contactConnectedDate(contact);
      connectedPeople.add(contactId);
      edges.push({
        id: `${organisationId}:person:${contactId}`,
        source: `person:${contactId}`,
        target: organisationId,
        type: "affiliation",
        derived: true,
        evidenceType: "extracted",
        approvalState: "derived",
        confidence: 0.92,
        sourcePath: `contacts/${contact.id}`,
        evidenceExcerpt: [contact.title || contact.title2, organisation.canonicalName].filter(Boolean).join(" at "),
        connectedAt: connectedDate?.toISOString(),
        recencyBand: connectionRecency(connectedDate),
      });
    }
  }

  for (const contactId of connectedPeople) {
    const contact = contactById.get(contactId);
    if (!contact) continue;
    const connectionCount = edges.filter((edge) => edge.source === `person:${contactId}`).length;
    nodes.push({
      id: `person:${contact.id}`,
      type: "person",
      label: contact.displayName,
      subtitle: contact.title || contact.title2 || contact.company || "Contact",
      contact,
      connectionCount,
    });
  }

  return { nodes, edges };
}
